import { useEffect, useState } from "react";
import {
  CheckCircle2,
  Clock,
  XCircle,
  Undo2,
  AlertCircle,
  Calendar,
  CreditCard,
  User as UserIcon,
  ShoppingCart,
} from "lucide-react";
import { Modal } from "@components/ui/Modal";
import { Button } from "@components/ui/Button";
import { orderService } from "../../../services/orderService";
import type { Order, OrderStatus } from "../../../types/order";

interface OrderDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string | null;
  initialOrder?: Order | null;
}

const STATUS_BADGES: Record<OrderStatus, { bg: string; text: string; label: string }> = {
  paid: { bg: "bg-green-100", text: "text-green-700", label: "Paid" },
  pending: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Pending" },
  cancelled: { bg: "bg-gray-200", text: "text-gray-700", label: "Cancelled" },
  expired: { bg: "bg-orange-100", text: "text-orange-700", label: "Expired" },
  refunded: { bg: "bg-red-100", text: "text-red-700", label: "Refunded" },
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrency = (value: number, currency: string | null): string => {
  return `${value.toFixed(2)} ${currency || "USD"}`;
};

interface TimelineRow {
  label: string;
  timestamp: string | null;
  icon: React.ReactNode;
  iconColor: string;
}

export function OrderDetailsDialog({
  isOpen,
  onClose,
  orderId,
  initialOrder,
}: OrderDetailsDialogProps) {
  const [order, setOrder] = useState<Order | null>(initialOrder ?? null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (initialOrder) {
        setOrder(initialOrder);
        setIsLoading(false);
      }
      setError(null);
      if (orderId && (!initialOrder || initialOrder._id !== orderId)) {
        setIsLoading(true);
        setOrder(null);
        orderService
          .get(orderId)
          .then((response) => {
            setOrder(response.data.order);
          })
          .catch((err: unknown) => {
            const message =
              (err as { message?: string })?.message ||
              "Failed to load order details.";
            setError(message);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }
  }, [isOpen, orderId, initialOrder]);

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Order Details"
      className="max-w-3xl"
    >
      {isLoading && (
        <div className="py-12 text-center text-gray-500">
          <div className="animate-spin inline-block w-6 h-6 border-[3px] border-current border-t-transparent text-indigo-600 rounded-full" />
          <p className="mt-2">Loading order...</p>
        </div>
      )}

      {!isLoading && error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {!isLoading && !error && order && (
        <OrderBody order={order} />
      )}

      <div className="flex justify-end pt-6 border-t border-gray-100 mt-6">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
}

function OrderBody({ order }: { order: Order }) {
  const badge = STATUS_BADGES[order.status] || STATUS_BADGES.pending;
  const user = renderOrderUser(order.user);

  return (
    <div className="space-y-6">
      {/* Header */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between p-4 bg-gray-50 rounded-lg">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-wider text-gray-400 font-bold">
            Order
          </p>
          <p className="font-mono text-sm text-gray-900 break-all">
            {order._id}
          </p>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <UserIcon size={14} aria-hidden />
            <span className="font-medium text-[#102040]">{user.name}</span>
            {user.email && (
              <span className="text-gray-400">({user.email})</span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-start gap-2 sm:items-end">
          <span
            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}
          >
            <StatusGlyph status={order.status} />
            {badge.label}
          </span>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Calendar size={12} aria-hidden />
            {formatDateTime(order.createdAt)}
          </div>
        </div>
      </header>

      {/* Line items */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <ShoppingCart size={14} aria-hidden />
          Line items
        </h3>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-600">
                  Course
                </th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">
                  Unit price
                </th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">
                  Qty
                </th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">
                  Subtotal
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {order.lineItems.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-6 text-center text-gray-400 italic"
                  >
                    No line items
                  </td>
                </tr>
              ) : (
                order.lineItems.map((item) => (
                  <tr key={item.courseId}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {item.courseSnapshot?.thumbnail ? (
                          <img
                            src={item.courseSnapshot.thumbnail}
                            alt={item.courseSnapshot.title}
                            className="w-10 h-10 rounded object-cover bg-gray-100"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded bg-gray-100" />
                        )}
                        <div>
                          <p className="font-medium text-[#102040]">
                            {item.courseSnapshot?.title || "(deleted course)"}
                          </p>
                          {item.courseSnapshot?.instructorName && (
                            <p className="text-xs text-gray-400">
                              {item.courseSnapshot.instructorName}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {formatCurrency(item.unitPriceUSD, "USD")}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {item.quantity}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-[#102040]">
                      {formatCurrency(
                        item.unitPriceUSD * item.quantity,
                        "USD",
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Totals */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <TotalsTile
          label="Subtotal"
          value={formatCurrency(order.subtotalUSD, "USD")}
        />
        <TotalsTile
          label={
            order.coupon
              ? `Coupon (${order.coupon.code})`
              : "Coupon discount"
          }
          value={
            order.discountUSD > 0
              ? `−${formatCurrency(order.discountUSD, "USD")}`
              : "—"
          }
          accent={order.discountUSD > 0 ? "green" : "muted"}
        />
        <TotalsTile
          label="Total"
          value={formatCurrency(order.totalUSD, order.currency)}
          accent={order.discountUSD > 0 ? "amber" : "default"}
        />
      </section>

      {/* Enrollments */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <ShoppingCart size={14} aria-hidden />
          Enrollments
        </h3>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-600">
                  Course
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-600">
                  Enrollment ID
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-600">
                  Status
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-600">
                  Payment
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-600">
                  Refunded
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {order.enrollmentIds.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-6 text-center text-gray-400 italic"
                  >
                    No enrollment data available
                  </td>
                </tr>
              ) : (
                order.enrollmentIds.map((enrollment) => {
                  if (typeof enrollment === "string") {
                    return (
                      <tr key={enrollment}>
                        <td className="px-4 py-3 text-gray-400 italic">
                          Loading…
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-700">
                          {enrollment}
                        </td>
                        <td className="px-4 py-3 text-gray-400 italic">
                          —
                        </td>
                        <td className="px-4 py-3 text-gray-400 italic">
                          —
                        </td>
                        <td className="px-4 py-3 text-gray-400 italic">
                          —
                        </td>
                      </tr>
                    );
                  }
                  const course = enrollment.course;
                  const courseTitle =
                    typeof course === "object" && course !== null
                      ? course.title
                      : "(deleted course)";
                  const paymentStatus =
                    enrollment.paymentInfo?.paymentStatus || "—";
                  const isRefunded =
                    enrollment.paymentInfo?.refund?.isRefunded === true;
                  return (
                    <tr key={enrollment._id}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-[#102040]">
                          {courseTitle}
                        </p>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-700">
                        {enrollment._id}
                      </td>
                      <td className="px-4 py-3">
                        <EnrollmentStatusBadge status={enrollment.status} />
                      </td>
                      <td className="px-4 py-3">
                        <PaymentStatusBadge status={paymentStatus} />
                      </td>
                      <td className="px-4 py-3">
                        {isRefunded ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            <Undo2 size={10} aria-hidden />
                            Refunded
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Gateway + refs */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <CreditCard size={14} aria-hidden />
          Gateway
        </h3>
        {!order.gateway ? (
          <p className="text-sm text-gray-500 italic">No gateway on file.</p>
        ) : (
          <div>
            {order.gateway === "paytabs" && order.gatewayRefs?.paytabsTranRef && (
              <Field
                label="PayTabs Reference"
                value={order.gatewayRefs.paytabsTranRef}
                mono
              />
            )}
            {order.gateway === "paytabs" && order.gatewayRefs?.paytabsRefundTranRef && (
              <Field
                label="PayTabs Refund Reference"
                value={order.gatewayRefs.paytabsRefundTranRef}
                mono
              />
            )}
            {order.gateway === "legacy" && (
              <p className="text-sm text-gray-500 italic">
                Paid through a retired gateway. Refund via the provider dashboard,
                then update the payment status manually.
              </p>
            )}
          </div>
        )}
      </section>

      {/* Timeline */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Timeline</h3>
        <ol className="space-y-2">
          {buildTimeline(order).map((row) => (
            <li
              key={row.label}
              className="flex items-center gap-3 text-sm"
            >
              <span
                className={`flex items-center justify-center w-7 h-7 rounded-full ${row.iconColor}`}
              >
                {row.icon}
              </span>
              <div className="flex-1 flex items-center justify-between gap-3">
                <span className="font-medium text-gray-700">{row.label}</span>
                <span className="text-gray-500 text-xs">
                  {formatDateTime(row.timestamp)}
                </span>
              </div>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function buildTimeline(order: Order): TimelineRow[] {
  return [
    {
      label: "Created",
      timestamp: order.createdAt,
      icon: <Clock size={14} aria-hidden />,
      iconColor: "bg-blue-100 text-blue-600",
    },
    {
      label: "Paid",
      timestamp: order.paidAt,
      icon: <CheckCircle2 size={14} aria-hidden />,
      iconColor: order.paidAt
        ? "bg-green-100 text-green-600"
        : "bg-gray-100 text-gray-400",
    },
    {
      label: "Cancelled",
      timestamp: order.cancelledAt,
      icon: <XCircle size={14} aria-hidden />,
      iconColor: order.cancelledAt
        ? "bg-gray-200 text-gray-700"
        : "bg-gray-100 text-gray-400",
    },
    {
      label: "Expired",
      timestamp: order.expiredAt,
      icon: <AlertCircle size={14} aria-hidden />,
      iconColor: order.expiredAt
        ? "bg-orange-100 text-orange-600"
        : "bg-gray-100 text-gray-400",
    },
    {
      label: "Refunded",
      timestamp: order.refundedAt,
      icon: <Undo2 size={14} aria-hidden />,
      iconColor: order.refundedAt
        ? "bg-red-100 text-red-600"
        : "bg-gray-100 text-gray-400",
    },
  ];
}

function StatusGlyph({ status }: { status: OrderStatus }) {
  switch (status) {
    case "paid":
      return <CheckCircle2 size={14} aria-hidden />;
    case "pending":
      return <Clock size={14} aria-hidden />;
    case "cancelled":
      return <XCircle size={14} aria-hidden />;
    case "expired":
      return <AlertCircle size={14} aria-hidden />;
    case "refunded":
      return <Undo2 size={14} aria-hidden />;
    default:
      return null;
  }
}

const ENROLLMENT_STATUS_BADGES: Record<
  string,
  { bg: string; text: string; label: string }
> = {
  active: { bg: "bg-green-100", text: "text-green-700", label: "Active" },
  completed: { bg: "bg-blue-100", text: "text-blue-700", label: "Completed" },
  pending: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Pending" },
  cancelled: {
    bg: "bg-gray-200",
    text: "text-gray-700",
    label: "Cancelled",
  },
  expired: {
    bg: "bg-orange-100",
    text: "text-orange-700",
    label: "Expired",
  },
};

function EnrollmentStatusBadge({ status }: { status: string }) {
  const badge = ENROLLMENT_STATUS_BADGES[status] || {
    bg: "bg-gray-100",
    text: "text-gray-700",
    label: status || "—",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}
    >
      {badge.label}
    </span>
  );
}

const PAYMENT_STATUS_BADGES: Record<
  string,
  { bg: string; text: string; label: string }
> = {
  completed: { bg: "bg-green-100", text: "text-green-700", label: "Completed" },
  pending: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Pending" },
  failed: { bg: "bg-red-100", text: "text-red-700", label: "Failed" },
  refunded: { bg: "bg-red-100", text: "text-red-700", label: "Refunded" },
  free: { bg: "bg-blue-100", text: "text-blue-700", label: "Free" },
  cancelled: {
    bg: "bg-gray-200",
    text: "text-gray-700",
    label: "Cancelled",
  },
};

function PaymentStatusBadge({ status }: { status: string }) {
  const badge = PAYMENT_STATUS_BADGES[status] || {
    bg: "bg-gray-100",
    text: "text-gray-700",
    label: status || "—",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}
    >
      {badge.label}
    </span>
  );
}

function TotalsTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: "default" | "green" | "amber" | "muted";
}) {
  let accentClass = "text-gray-900";
  switch (accent) {
    case "green":
      accentClass = "text-green-700";
      break;
    case "amber":
      accentClass = "text-amber-700";
      break;
    case "muted":
      accentClass = "text-gray-400";
      break;
    default:
      accentClass = "text-gray-900";
  }
  return (
    <div className="p-3 bg-white border border-gray-200 rounded-lg">
      <p className="text-xs uppercase tracking-wider text-gray-400 font-bold">
        {label}
      </p>
      <p className={`mt-1 text-lg font-semibold ${accentClass}`}>{value}</p>
    </div>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="p-3 bg-gray-50 rounded-lg">
      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">
        {label}
      </p>
      <p
        className={`mt-1 text-xs break-all ${mono ? "font-mono text-gray-700" : "text-gray-800"}`}
      >
        {value}
      </p>
    </div>
  );
}

function renderOrderUser(user: Order["user"]): {
  name: string;
  email: string;
} {
  if (typeof user === "string") {
    return { name: "Unknown", email: "" };
  }
  const first = user.firstName || "";
  const last = user.lastName || "";
  const fullName = `${first} ${last}`.trim();
  return {
    name: fullName || user.email || "Unknown",
    email: user.email || "",
  };
}
